import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, History, Plus, Globe, Volume2, VolumeX, Square, Mic } from 'lucide-react';
import { ActionCard } from './ActionCard';
import { splitActions, speakable } from './drGActions';
import { MiniMarkdown } from './MiniMarkdown';
import { DrDanInput } from './DrDanInput';
import { DrDanHistory } from './DrDanHistory';
import { drDanApi } from './drDanApi';
import { DrGVoiceMode } from './DrGVoiceMode';
import { GotoButton } from './GotoButton';

const HIDDEN = ['/signup', '/admin', '/p/', '/portal', '/smile-app', '/practcom-upgrade', '/jobs', '/sign', '/payment', '/reset-password', '/set-password', '/onspace', '/landing'];
const SESSION_KEY = 'drDanSessionId';
const AVATAR = '/dr-g-photo-v2.jpg';
const Avatar = ({ size = 'h-8 w-8' }) => <img src={AVATAR} alt="Dr. G" className={`${size} rounded-full object-cover bg-white border border-sky-100 shrink-0`} />;
const SUGGESTIONS = [
  'What does the Professional plan include?',
  'How do I send post-op instructions by text?',
  'Find an oral surgeon in Saint James, NY',
  'Look up Jane Smith',
];

const isPracticeUser = () => {
  try {
    const { role, exp } = JSON.parse(atob(localStorage.getItem('dentalToken').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return ['practice_admin', 'practice_staff'].includes(role) && exp * 1000 > Date.now();
  } catch { return false; }
};

const speak = (text, onEnd) => {
  const synth = window.speechSynthesis;
  if (!synth) return;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(speakable(text));
  const voices = synth.getVoices();
  u.voice = voices.find((v) => /en-US/i.test(v.lang) && /male|david|guy|daniel|alex/i.test(v.name)) || voices.find((v) => /en-US/i.test(v.lang)) || null;
  u.rate = 1.02;
  u.onend = onEnd;
  u.onerror = onEnd;
  synth.speak(u);
};

const SpeakBtn = ({ text, idx }) => {
  const [on, setOn] = useState(false);
  if (!window.speechSynthesis) return null;
  const toggle = () => { if (on) { window.speechSynthesis.cancel(); setOn(false); } else { setOn(true); speak(text, () => setOn(false)); } };
  return (
    <button type="button" onClick={toggle} data-testid={`dr-g-speak-btn-${idx}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-sky-700">
      {on ? <><Square className="w-3 h-3" /> Stop</> : <><Volume2 className="w-3.5 h-3.5" /> Listen</>}
    </button>
  );
};

const Bubble = ({ m, idx, sessionId, streaming }) => {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl px-4 py-2.5 bg-sky-600 text-white text-[14px] whitespace-pre-wrap" data-testid="dr-dan-msg-user">{m.content}</div>
      </div>
    );
  }
  const { clean, actions } = splitActions(m.content);
  return (
    <div className="flex justify-start gap-2">
      <Avatar />
      <div className="max-w-[85%] min-w-0 space-y-2">
        {clean && <div className="rounded-2xl px-4 py-2.5 bg-slate-100 text-slate-800" data-testid="dr-dan-msg-assistant"><MiniMarkdown text={clean} /></div>}
        {!streaming && actions.filter((a) => a.type !== 'goto').map((a, i) => <ActionCard key={i} action={a} doneKey={`${sessionId}:${idx}:${i}`} />)}
        {!streaming && actions.some((a) => a.type === 'goto') && (
          <div data-testid="dr-g-goto-buttons">
            {actions.filter((a) => a.type === 'goto').map((a, i) => (
              <GotoButton key={i} action={a} onGo={() => window.dispatchEvent(new Event('practcom:drg-navigated'))} />
            ))}
          </div>
        )}
        {!streaming && clean && <SpeakBtn text={clean} idx={idx} />}
      </div>
    </div>
  );
};

const Welcome = ({ onPick }) => (
  <div className="space-y-3" data-testid="dr-dan-welcome">
    <Bubble idx="welcome" m={{ role: 'assistant', content: "Hi, I'm **Dr. G**. Ask me anything about PractCom (features, how-to, plans and prices) or any dental question. I can search the internet (for example to find a specialist near you), look up patients, send patient messages and save referral dentists. You always confirm before anything is sent or saved." }} />
    <div className="flex flex-wrap gap-2 pl-10">
      {SUGGESTIONS.map((s) => (
        <button key={s} type="button" onClick={() => onPick(s)} className="text-xs rounded-full border border-sky-200 bg-white text-sky-800 px-3 py-1.5 hover:bg-sky-50 transition-colors">{s}</button>
      ))}
    </div>
  </div>
);

export const DrDanWidget = () => {
  const { pathname } = useLocation();
  const [allowed, setAllowed] = useState(isPracticeUser);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const closeOnPhone = () => { if (window.innerWidth < 640) setOpen(false); };
    window.addEventListener('practcom:drg-navigated', closeOnPhone);
    return () => window.removeEventListener('practcom:drg-navigated', closeOnPhone);
  }, []);
  const [view, setView] = useState('chat');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY));
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState('');
  const [sending, setSending] = useState(false);
  const [autoRead, setAutoRead] = useState(() => localStorage.getItem('drGAutoRead') === '1');
  const endRef = useRef(null);

  useEffect(() => { const t = setInterval(() => setAllowed(isPracticeUser()), 2000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);
  useEffect(() => {
    if (!open || !sessionId || messages.length) return;
    drDanApi.session(sessionId).then((d) => setMessages(d.messages)).catch(() => { localStorage.removeItem(SESSION_KEY); setSessionId(null); });
  }, [open, sessionId, messages.length]);

  if (!allowed || HIDDEN.some((p) => pathname.startsWith(p))) return null;

  const pickSession = (id) => { setSessionId(id); id ? localStorage.setItem(SESSION_KEY, id) : localStorage.removeItem(SESSION_KEY); setMessages([]); setView('chat'); };

  const send = async (text) => {
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setBusy('Thinking…');
    setSending(true);
    const append = (chunk) => setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
      return copy;
    });
    try {
      await drDanApi.chat(text, sessionId, (ev) => {
        if (ev.type === 'session') { setSessionId(ev.id); localStorage.setItem(SESSION_KEY, ev.id); }
        else if (ev.type === 'tool') setBusy('Searching the web…');
        else if (ev.type === 'delta') { setBusy(''); append(ev.text); }
        else if (ev.type === 'error') append(`\n\n${ev.message}`);
      });
    } catch (e) {
      append(e.message);
    } finally {
      setBusy('');
      setSending(false);
      if (localStorage.getItem('drGAutoRead') === '1') {
        setMessages((m) => { const last = m[m.length - 1]; if (last?.role === 'assistant') speak(last.content); return m; });
      }
    }
  };

  if (!open) {
    return (
      <div className="fixed z-40 bottom-[calc(1.25rem+env(safe-area-inset-bottom))] right-4 sm:right-5 flex items-center gap-3">
        <span className="hidden sm:block bg-white text-sky-700 text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg border border-slate-100 pointer-events-none">Need Help? Ask Dr. G</span>
        <button type="button" onClick={() => setOpen(true)} data-testid="dr-dan-open-btn" aria-label="Ask Dr. G"
          className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white shadow-xl ring-4 ring-sky-500 overflow-hidden flex items-center justify-center transition-transform hover:scale-105">
          <img src={AVATAR} alt="Dr. G" className="h-full w-full object-cover" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="dr-dan-panel"
      className="fixed z-[70] inset-0 h-[100dvh] sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[min(440px,calc(100vw-2.5rem))] sm:h-[min(640px,calc(100vh-2.5rem))] bg-white sm:rounded-2xl shadow-2xl sm:border border-slate-200 flex flex-col overflow-hidden">
      <div className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] bg-sky-700 text-white">
        <Avatar size="h-10 w-10" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold leading-tight">Dr. G</p>
          <p className="text-[11px] text-sky-100 flex items-center gap-1">{view === 'voice' ? <><Mic className="w-3 h-3" /> Hands-free mode · just talk</> : <><Globe className="w-3 h-3" /> PractCom expert · can search the web</>}</p>
        </div>
        <button type="button" onClick={() => setView(view === 'voice' ? 'chat' : 'voice')}
          data-testid="dr-g-voice-command-btn" title={view === 'voice' ? 'Back to chat' : 'Hands-free mode (send instructions, consents, Med Cards and more by voice)'}
          className={`p-2 rounded-full hover:bg-white/10 ${view === 'voice' ? 'bg-white/20' : ''}`}><Mic className="w-4 h-4" /></button>
        <button type="button" onClick={() => { const v = !autoRead; setAutoRead(v); localStorage.setItem('drGAutoRead', v ? '1' : '0'); if (!v) window.speechSynthesis?.cancel(); }}
          data-testid="dr-g-autoread-btn" title={autoRead ? 'Reading answers aloud (tap to turn off)' : 'Read answers aloud'}
          className={`p-2 rounded-full hover:bg-white/10 ${autoRead ? 'bg-white/20' : ''}`}>{autoRead ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
        <button type="button" onClick={() => setView(view === 'history' ? 'chat' : 'history')} data-testid="dr-dan-history-btn" title="Past conversations" className="p-2 rounded-full hover:bg-white/10"><History className="w-4 h-4" /></button>
        <button type="button" onClick={() => pickSession(null)} data-testid="dr-dan-new-chat-btn" title="New conversation" className="p-2 rounded-full hover:bg-white/10"><Plus className="w-4 h-4" /></button>
        <button type="button" onClick={() => setOpen(false)} data-testid="dr-dan-close-btn" title="Close" className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      {view === 'voice' ? (
        <DrGVoiceMode Avatar={Avatar} />
      ) : view === 'history' ? (
        <div className="flex-1 overflow-y-auto"><DrDanHistory onOpen={pickSession} activeId={sessionId} onDeleted={(id) => id === sessionId && pickSession(null)} /></div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f8fbfd]" data-testid="dr-dan-messages">
            {!messages.length && <Welcome onPick={(q) => !sending && send(q)} />}
            {messages.map((m, i) => (m.content ? <Bubble key={i} m={m} idx={i} sessionId={sessionId} streaming={sending && i === messages.length - 1} /> : null))}
            {busy && <p className="pl-10 text-xs text-slate-500 animate-pulse" data-testid="dr-dan-busy">{busy}</p>}
            <div ref={endRef} />
          </div>
          <DrDanInput onSend={send} disabled={sending} />
        </>
      )}
    </div>
  );
};
